import React, { useRef, useState, memo, useEffect } from "react";
import { baseUrl, isAllowedDomain } from "../../utils/helpers/helper";
import * as styles from "./css/ContactForm.module.css";
import { RenderSvg } from "../../utils/helpers/helper";
import useConfirmationPopup from "./useConfirmationPopUp";
import ReCAPTCHA from "react-google-recaptcha";
import { siteKey } from "../../utils/helpers/helper";
import {
  trackContactFormClosed,
  trackContactFormOpened,
  trackContactFormSubmitted,
} from "../../utils/GA4Tracking";

const isValidEmail = (email) => {
  if (!email) return false;
  return (
    email.includes("@") &&
    email.split("@")[1] &&
    email.split("@")[1].split(".")[1] &&
    email.split("@")[1].split(".")[1]?.length > 1 &&
    isAllowedDomain(email)
  );
};

const isValidPhoneNumber = (phoneNumber) => {
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, "");

  // Check if it's a valid Indian mobile number
  if (digits.length === 10) {
    // 10-digit number
    return /^[6-9]\d{9}$/.test(digits);
  } else if (digits.length === 11 && digits.startsWith("0")) {
    // 11-digit number starting with 0
    return /^0[6-9]\d{9}$/.test(digits);
  } else if (digits.length === 12 && digits.startsWith("91")) {
    // 12-digit number starting with 91
    return /^91[6-9]\d{9}$/.test(digits);
  } else if (digits.length === 13 && digits.startsWith("91")) {
    // 13-digit number starting with +91 (+ is removed in digits)
    return /^91[6-9]\d{9}$/.test(digits);
  }

  return false;
};

const ContactForm2 = memo(
  ({
    isMobile,
    handelContactForm,
    handelCheckFormIsFilled = () => {},
    formIsFilled,
    header,
    contactForm,
    openPopup,
  }) => {
    const [success, setSuccess] = useState(null);
    const submitButtonRef = useRef(null);
    const formRef = useRef(null);
    const [showConfirmation, ConfirmationPopUpComponent] =
      useConfirmationPopup();
    const [token, setToken] = useState(null);
    const inputRef = useRef(null);
    const [btnValid, setBtnValid] = useState(false);
    const recaptchaRef = useRef(null);

    useEffect(() => {
      if (openPopup === true || contactForm === true) {
        inputRef.current.focus();
        const from = contactForm ? "Contact us" : "Header";
        trackContactFormOpened(from);
      }
    }, [openPopup, contactForm]);

    const validateCaptcha = (token) => {
      setToken(token);
      // console.log("token", token);
    };

    useEffect(() => {
      if (isValidEmail && isValidPhoneNumber && token) {
        setBtnValid(true);
      }
    }, [isValidEmail, isValidPhoneNumber, token]);

    const handleSubmit = async (event) => {
      event.preventDefault();
      submitButtonRef.current.innerHTML = "Submitting...";

      const formData = new FormData(event.target);
      //const name = formData.get("Name").trim();
      const phoneNumber = formData.get("PhoneNumber").trim();
      const yourEmail = formData.get("Email").trim();
      const textarea = formData.get("textArea").trim();

      // Reset custom validity
      //event.target.Name.setCustomValidity("");
      event.target.Email.setCustomValidity("");
      event.target.PhoneNumber.setCustomValidity("");
      event.target.textArea.setCustomValidity("");

      // Check for errors
      if (!isValidEmail(yourEmail)) {
        event.target.Email.setCustomValidity(
          "Please enter a valid email Id"
        );
        event.target.Email.reportValidity();
        submitButtonRef.current.innerHTML = "Submit";
        return;
      }

      if (!isValidPhoneNumber(phoneNumber)) {
        event.target.PhoneNumber.setCustomValidity(
          "Please enter a valid Indian phone number"
        );
        event.target.PhoneNumber.reportValidity();
        submitButtonRef.current.innerHTML = "Submit";
        return;
      }

      if (!textarea) {
        event.target.textArea.setCustomValidity("Please enter your message");
        event.target.textArea.reportValidity();
        submitButtonRef.current.innerHTML = "Submit";
        return;
      }

      if (!token) {
        submitButtonRef.current.innerHTML = "Submit";
        return;
      }

      try {
        const response = await fetch(`${baseUrl}/api/contact-form-partnerships`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: {
             // Name: name,
              PhoneNumber: phoneNumber,
              Email: yourEmail,
              textArea: textarea,
              token: token,
            },
          }),
        });

        if (response.ok) {
          const from = contactForm ? "Contact us" : "Header";
          trackContactFormSubmitted(from);
          submitButtonRef.current.innerHTML = "Submitted";
          setSuccess("Contact created successfully!");
          handelCheckFormIsFilled(false);
          // Close the form after a short delay

          setTimeout(() => {
            closeForm(true);
            // Reset the reCAPTCHA
            setToken(null);
            if (recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
            window.parent.postMessage({ eventName: "formSubmitted" }, "*");
          }, 2000); // 2 second delay
        } else {
          const errorMessage = await response.text();
          const { error } = JSON.parse(errorMessage);

          if (error && error.message.includes("yourEmail")) {
            event.target.Email.setCustomValidity(
              "Please enter a valid email Id"
            );
            event.target.Email.reportValidity();
          } else {
            throw new Error(error?.message || "An error occurred");
          }
        }
      } catch (error) {
        console.error("Error creating contact:", error.message);
      } finally {
        submitButtonRef.current.innerHTML = "Submit";
      }
    };

    const handleInputChange = (e) => {
      e.target.setCustomValidity(""); // Clear any custom validity message
      const isFilled = formRef.current;
      handelCheckFormIsFilled(true);
    };

    const clearForm = () => {
      if (formRef.current) {
        formRef.current.reset();
      }
      setSuccess(null);
      handelCheckFormIsFilled(false);
    };

    const closeForm = async (isFilled = false) => {
      if (formIsFilled && isFilled === false) {
        try {
          await showConfirmation();
          clearForm();
          handelContactForm(false);
        } catch {
          console.warn("Form close action was cancelled by the user.");
        }
      } else {
        const from = contactForm ? "Contact us" : "Header";
        trackContactFormClosed(from);
        clearForm();
        handelContactForm(false);
      }
    };

    return (
      <>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={styles?.contactForm}
          noValidate
        >
          <div className={styles.mainContainer}>
            {!header && (
              <div
                className={styles.closeIcon}
                onClick={() => {
                  closeForm();
                }}
              >
                <RenderSvg
                  IconName="times"
                  IconType="light"
                  style={{
                    height: "14px",
                    fill: "black",
                  }}
                />
              </div>
            )}
            <div className={styles.ContactFormWrap}>
            <div className={styles.contactFormHead}>Contact Us</div>
              <div className={styles?.contactFormInput}>
              {/*<input
                  ref={inputRef}
                  type="name"
                  name="Name"
                  placeholder="Name*"
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                />*/}
                <input
                  ref={inputRef}
                  type="email"
                  name="Email"
                  placeholder="Email ID*"
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                />
                <input
                  type="tel"
                  name="PhoneNumber"
                  placeholder="Phone Number*"
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                  pattern="(\+91|0)?[6-9]\d{9}"
                  title="Please enter a valid phone number"
                />
                  <div className={styles?.contactTextArea}>
                  <textarea
                    name="textArea"
                    rows={1}
                    cols={2}
                    onChange={handleInputChange}
                    placeholder={
                      isMobile
                        ? "What are you looking for?"
                        : "What are you looking for?"
                    }
                    required
                    autoComplete="off"
                  />
                  </div>
                  {/*} <label htmlFor="resumeUpload">
                         Upload Resume (PDF/DOC)
                    </label>
                    <input
                          type="file"
                          name="resume"
                          id="resumeUpload"
                          accept=".pdf, .doc, .docx"
                          onChange={handleInputChange}
                          required
                    />*/}
                  <div className={styles.captcha}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      onChange={validateCaptcha}
                      sitekey={siteKey}
                      theme="light"
                      size="normal"
                    />
                  </div>

                  <button
                    disabled={!btnValid}
                    ref={submitButtonRef}
                    className={`${styles?.ContactBtn} banner-secondary-sm`}
                    type="submit"
                  >
                    Send
                  </button>
                
              </div>
              {success && (
                <div
                  className={styles.successMessage}
                  style={{ color: "green" }}
                >
                  {success}
                </div>
              )}
            </div>
          </div>
          <div>{ConfirmationPopUpComponent}</div>
        </form>
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.formIsFilled === nextProps.formIsFilled &&
    prevProps.header === nextProps.header &&
    prevProps.openPopup === nextProps.openPopup
);

export default ContactForm2;
